import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { Company } from '@src/@data/types/company';
import { CompaniesCache } from './CompaniesCache';
import { NotImplemented } from '@src/@data/utility/NotImplemented';

interface ContextValueType {
  getCachedCompanies: () => Promise<Company[]>;
  getCachedCompany: (companyId: string) => Promise<Company>;
  invalidateCachedCompanies: () => void;
}

export const CachedCompaniesContext = createContext<ContextValueType>({
  getCachedCompanies: NotImplemented,
  getCachedCompany: NotImplemented,
  invalidateCachedCompanies: NotImplemented,
});

export const useCachedCompanies = () => useContext(CachedCompaniesContext);

export const CachedCompaniesProvider: React.FC = ({ children }) => {
  const cache = useMemo(() => new CompaniesCache(), []);

  const getCachedCompanies = useCallback((): Promise<Company[]> => {
    return cache.getData();
  }, [cache]);

  const getCachedCompany = useCallback(
    (companyId: string): Promise<Company> => {
      return getCachedCompanies().then((companies) => {
        const company = companies.find((company) => company.id === companyId);
        return company || Promise.reject('Not found');
      });
    },
    [getCachedCompanies]
  );

  const invalidateCachedCompanies = useCallback(() => {
    cache.invalidate();
  }, [cache]);

  return (
    <CachedCompaniesContext.Provider
      value={{
        getCachedCompanies,
        getCachedCompany,
        invalidateCachedCompanies,
      }}
    >
      {children}
    </CachedCompaniesContext.Provider>
  );
};
